"use client";

import type { ReactNode, ElementType } from "react";
import { cn } from "@drpcrd/utils";
import { LucideIcon } from "lucide-react";
import { GradientIcon } from "../icons/index";
import { CustomSpinner } from "../icons/custom-spinner";

export type SuggestionProps = {
  onClick?: () => void;
  className?: string;
  children: ReactNode;
  icon?: LucideIcon;
  href?: string;
  as?: ElementType;
  disabled?: boolean;
  loading?: boolean;
} & Record<string, unknown>; // Allow any additional props to be passed through

/**
 * This button works similarly to the base Button component but to achieve the
 * gradient border effect we need to create a custom component which allows
 * for more flexibility with the styles.
 */
export const GradientButton = ({
  onClick,
  className,
  children,
  icon: Icon,
  disabled,
  loading = false,
  as: Component = "button",
  href,
  ...props
}: SuggestionProps) => (
  <Component
    className={cn(
      "flex rounded-full border-none bg-neutral-500 p-px text-sm leading-2.75 transition-all",
      (onClick || href) &&
        !disabled &&
        !loading &&
        "hover:from-primary hover:shadow-light cursor-pointer hover:bg-gradient-to-r hover:to-pink-500",
      (disabled || loading) && "pointer-events-none opacity-50",
      className,
    )}
    href={href}
    onClick={onClick}
    disabled={disabled || loading}
    {...props}
  >
    <div className="flex items-center gap-1 rounded-full bg-white px-2 py-1">
      {loading ? (
        <CustomSpinner className="h-2 w-2 animate-spin" />
      ) : (
        Icon && <GradientIcon icon={Icon} size={16} />
      )}
      {children}
    </div>
  </Component>
);
